import React, { useState, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import UserLayout from '../../components/Layout/UserLayout';
import {
  Card, Button, Badge, SearchInput, Select, Spinner, EmptyState,
  Table, TableHead, TableBody, TableRow, TableCell, Modal
} from '../../components/UI';
import api, { callLogsAPI } from '../../services/api';
import toast from 'react-hot-toast';
import { HiOutlinePhone, HiOutlinePlay, HiOutlineDownload } from 'react-icons/hi';
import { format } from 'date-fns';

const statusOptions = [
  { value: '', label: 'All Status' },
  { value: 'connected', label: 'Connected' },
  { value: 'not_connected', label: 'Not Connected' },
  { value: 'busy', label: 'Busy' },
  { value: 'no_answer', label: 'No Answer' },
];

// Build compact pagination with ellipsis
const buildPageList = (current, total) => {
  const pages = [];
  const set = new Set([1, total, current, current - 1, current + 1]);
  for (const p of set) {
    if (p >= 1 && p <= total) pages.push(p);
  }
  pages.sort((a, b) => a - b);

  const result = [];
  for (let i = 0; i < pages.length; i++) {
    result.push(pages[i]);
    if (i < pages.length - 1 && pages[i + 1] - pages[i] > 1) {
      result.push('...');
    }
  }
  return result;
};

const UserCallLogs = () => {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [selectedLog, setSelectedLog] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const audioUrlRef = useRef(null);

  // Resolve the recording ID for playback
  const getRecordingId = (log) => {
    if (log.recordingPublicId) return log.recordingPublicId;
    if (log.recordingUrl) {
      const match = log.recordingUrl.match(/\/recordings\/file\/([a-f\d]{24})/i);
      if (match) return match[1];
    }
    return null;
  };

  // Fetch audio URL with authentication when a log is selected for playback
  const openRecordingModal = (log) => {
    setSelectedLog(log);
  };

  // Fetch audio when selectedLog changes
  React.useEffect(() => {
    const recordingId = selectedLog ? getRecordingId(selectedLog) : null;
    if (recordingId) {
      const fetchAudio = async () => {
        try {
          if (audioUrlRef.current) {
            URL.revokeObjectURL(audioUrlRef.current);
            audioUrlRef.current = null;
          }
          const response = await api.get(`/recordings/file/${recordingId}`, {
            responseType: 'blob'
          });
          const url = URL.createObjectURL(response.data);
          audioUrlRef.current = url;
          setAudioUrl(url);
        } catch (error) {
          console.error('Failed to load recording:', error);
          toast.error('Failed to load recording');
          setAudioUrl(null);
        }
      };
      fetchAudio();
    } else {
      if (audioUrlRef.current) {
        URL.revokeObjectURL(audioUrlRef.current);
        audioUrlRef.current = null;
      }
      setAudioUrl(null);
    }
    return () => {
      if (audioUrlRef.current) {
        URL.revokeObjectURL(audioUrlRef.current);
        audioUrlRef.current = null;
      }
    };
  }, [selectedLog?._id]);

  // Fetch call logs
  const { data: logsData, isLoading } = useQuery({
    queryKey: ['userCallLogs', page, pageSize, statusFilter],
    queryFn: async () => {
      const params = { page, limit: pageSize };
      if (statusFilter) params.status = statusFilter;
      const response = await callLogsAPI.getAll(params);
      return response.data || {};
    },
  });

  // Fetch statistics
  const { data: stats } = useQuery({
    queryKey: ['userCallStats'],
    queryFn: async () => {
      const response = await callLogsAPI.getStatistics();
      return response.data?.data || {};
    },
  });

  const logs = logsData?.data || [];
  const pagination = logsData?.pagination || {};

  const filteredLogs = search
    ? logs.filter(l =>
        l.phoneNumber?.includes(search) ||
        l.leadId?.name?.toLowerCase().includes(search.toLowerCase())
      )
    : logs;

  return (
    <UserLayout>
      <div className="p-4 lg:p-6 space-y-4 sm:space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4">
          <div className="min-w-0">
            <h1 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900">My Call Logs</h1>
            <p className="text-gray-500 text-xs sm:text-sm mt-0.5 sm:mt-1">View your call history and recordings</p>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-2 sm:gap-3 lg:gap-4">
          <Card padding="p-2.5 sm:p-3 lg:p-4" className="text-center">
            <p className="text-xs text-gray-500">Total Calls</p>
            <p className="text-base sm:text-lg lg:text-2xl font-bold text-gray-900">{stats?.total || 0}</p>
          </Card>
          <Card padding="p-2.5 sm:p-3 lg:p-4" className="text-center bg-green-50">
            <p className="text-xs text-green-600">Connected</p>
            <p className="text-base sm:text-lg lg:text-2xl font-bold text-green-600">{stats?.connected || 0}</p>
          </Card>
          <Card padding="p-2.5 sm:p-3 lg:p-4" className="text-center bg-blue-50">
            <p className="text-xs text-blue-600">Avg Duration</p>
            <p className="text-base sm:text-lg lg:text-2xl font-bold text-blue-600">
              {Math.floor((stats?.averageDuration || 0) / 60)}:{((stats?.averageDuration || 0) % 60).toString().padStart(2, '0')}
            </p>
          </Card>
        </div>

        {/* Filters */}
        <Card padding="p-3 sm:p-4">
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
            <div className="flex-1">
              <SearchInput
                value={search}
                onChange={setSearch}
                placeholder="Search by phone..."
              />
            </div>
            <Select
              options={statusOptions}
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="sm:w-40"
            />
          </div>
        </Card>

        {/* Call Logs */}
        {isLoading ? (
          <div className="flex justify-center py-12">
            <Spinner size="lg" />
          </div>
        ) : filteredLogs.length === 0 ? (
          <Card>
            <EmptyState
              icon={<HiOutlinePhone className="w-8 h-8 text-gray-400" />}
              title="No call logs found"
              description="Your call history will appear here"
            />
          </Card>
        ) : (
          <Card padding="p-0">
            {/* Desktop Table */}
            <div className="hidden md:block overflow-x-auto">
              <Table>
                <TableHead>
                  <TableRow hoverable={false}>
                    <TableCell header className="min-w-[100px]">Phone</TableCell>
                    <TableCell header className="min-w-[120px]">Lead</TableCell>
                    <TableCell header className="min-w-[90px]">Status</TableCell>
                    <TableCell header className="min-w-[70px]">Duration</TableCell>
                    <TableCell header className="min-w-[100px]">Date</TableCell>
                    <TableCell header className="min-w-[100px]">Recording</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredLogs.map((log) => (
                    <TableRow key={log._id}>
                      <TableCell>
                        <span className="font-medium text-gray-900 whitespace-nowrap">{log.phoneNumber}</span>
                      </TableCell>
                      <TableCell>
                        <span className="text-gray-600 truncate max-w-[150px] block">
                          {log.leadId?.name || '-'}
                        </span>
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={log.status === 'connected' ? 'success' : 'danger'}
                          size="sm"
                        >
                          {log.status?.replace('_', ' ')}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <span className="text-gray-600 whitespace-nowrap">
                          {log.duration
                            ? `${Math.floor(log.duration / 60)}:${(log.duration % 60).toString().padStart(2, '0')}`
                            : '-'}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className="text-gray-500 text-sm whitespace-nowrap">
                          {log.createdAt ? format(new Date(log.createdAt), 'MMM d, h:mm a') : '-'}
                        </span>
                      </TableCell>
                      <TableCell>
                        {(log.recordingPublicId || log.recordingUrl) ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="p-1.5"
                            icon={<HiOutlinePlay className="w-4 h-4 text-primary-600" />}
                            onClick={() => openRecordingModal(log)}
                            title="Play Recording"
                          />
                        ) : (
                          <span className="text-gray-400 text-sm">No recording</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {/* Mobile Cards */}
            <div className="md:hidden divide-y divide-gray-100">
              {filteredLogs.map((log) => (
                <div key={log._id} className="p-3 sm:p-4 hover:bg-gray-50 transition-colors">
                  {/* Header Row */}
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-gray-900">{log.phoneNumber}</p>
                      <p className="text-xs sm:text-sm text-gray-500 truncate">
                        {log.leadId?.name || 'Unknown Lead'}
                      </p>
                    </div>
                    <Badge
                      variant={log.status === 'connected' ? 'success' : 'danger'}
                      size="sm"
                    >
                      {log.status?.replace('_', ' ')}
                    </Badge>
                  </div>

                  {/* Info Row */}
                  <div className="flex items-center justify-between text-xs sm:text-sm mb-3">
                    <span className="text-gray-400">
                      {log.createdAt ? format(new Date(log.createdAt), 'MMM d, h:mm a') : '-'}
                    </span>
                    <span className="text-gray-500 font-medium">
                      {log.duration
                        ? `${Math.floor(log.duration / 60)}:${(log.duration % 60).toString().padStart(2, '0')}`
                        : '-'}
                    </span>
                  </div>

                  {/* Recording Button */}
                  {(log.recordingPublicId || log.recordingUrl) && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full"
                      icon={<HiOutlinePlay className="w-4 h-4 text-primary-600" />}
                      onClick={() => openRecordingModal(log)}
                    >
                      Play Recording
                    </Button>
                  )}
                </div>
              ))}
            </div>

            {/* Pagination */}
            {pagination.totalPages > 0 && (
              <div className="flex flex-col sm:flex-row items-center justify-between gap-3 p-3 sm:p-4 border-t border-gray-100">
                <div className="flex items-center gap-3 text-xs sm:text-sm text-gray-500">
                  <span>
                    Showing {((pagination.page - 1) * pageSize) + 1}–{Math.min(pagination.page * pageSize, pagination.total)} of {pagination.total}
                  </span>
                  <div className="flex items-center gap-1.5">
                    <label htmlFor="ucl-rows-per-page" className="text-xs sm:text-sm text-gray-500">Rows per page:</label>
                    <select
                      id="ucl-rows-per-page"
                      value={pageSize}
                      onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
                      className="px-2 py-1 border border-gray-200 rounded-md text-sm bg-white"
                    >
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                  </div>
                </div>
                {pagination.totalPages > 1 && (
                  <div className="flex items-center gap-1.5 sm:gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={page === 1}
                      onClick={() => setPage(p => p - 1)}
                    >
                      <span className="hidden sm:inline">Previous</span>
                      <span className="sm:hidden">Prev</span>
                    </Button>
                    {buildPageList(page, pagination.totalPages).map((item, idx) =>
                      item === '...' ? (
                        <span key={`ellipsis-${idx}`} className="px-2 text-sm text-gray-400 select-none">…</span>
                      ) : (
                        <button
                          key={item}
                          type="button"
                          onClick={() => setPage(item)}
                          className={
                            page === item
                              ? "min-w-[34px] px-2.5 py-1.5 rounded-lg text-sm font-semibold bg-primary-600 text-white transition-colors"
                              : "min-w-[34px] px-2.5 py-1.5 rounded-lg text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
                          }
                        >
                          {item}
                        </button>
                      )
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={page === pagination.totalPages}
                      onClick={() => setPage(p => p + 1)}
                    >
                      Next
                    </Button>
                  </div>
                )}
              </div>
            )}
          </Card>
        )}

        {/* Recording Modal */}
        <Modal
          isOpen={!!selectedLog}
          onClose={() => setSelectedLog(null)}
          title="Call Recording"
          size="md"
        >
          {selectedLog && (
            <div className="space-y-3 sm:space-y-4">
              <div className="bg-gray-50 p-3 sm:p-4 rounded-xl">
                <div className="flex items-start justify-between gap-2 mb-3 sm:mb-4">
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-gray-900 truncate">{selectedLog.phoneNumber}</p>
                    <p className="text-xs sm:text-sm text-gray-500 truncate">
                      {selectedLog.leadId?.name || 'Unknown Lead'}
                    </p>
                  </div>
                  <div className="flex flex-col items-end gap-1 flex-shrink-0">
                    <Badge
                      variant={
                        selectedLog.callType === 'incoming' ? 'info' :
                          selectedLog.callType === 'missed' ? 'danger' : 'primary'
                      }
                      size="sm"
                    >
                      {selectedLog.callType || 'outgoing'}
                    </Badge>
                    <Badge variant={selectedLog.status === 'connected' ? 'success' : 'danger'} size="sm">
                      {selectedLog.status}
                    </Badge>
                  </div>
                </div>
                <div className="text-xs sm:text-sm text-gray-500 mb-3">
                  {selectedLog.createdAt
                    ? format(new Date(selectedLog.createdAt), 'MMM d, yyyy h:mm a')
                    : '-'}
                  {' · '}
                  {selectedLog.duration
                    ? `${Math.floor(selectedLog.duration / 60)}:${(selectedLog.duration % 60).toString().padStart(2, '0')}`
                    : '0:00'}
                </div>
                {audioUrl ? (
                  <audio controls className="w-full" src={audioUrl}>
                    Your browser does not support the audio element.
                  </audio>
                ) : (
                  <div className="flex items-center justify-center py-4">
                    <Spinner size="sm" />
                    <span className="ml-2 text-gray-500 text-sm">Loading audio...</span>
                  </div>
                )}
              </div>
              <div className="flex justify-end">
                <Button
                  variant="outline"
                  size="sm"
                  icon={<HiOutlineDownload className="w-4 h-4" />}
                  onClick={async () => {
                    try {
                      const recId = getRecordingId(selectedLog);
                      if (!recId) {
                        toast.error('No recording available');
                        return;
                      }
                      const response = await api.get(`/recordings/file/${recId}`, {
                        responseType: 'blob'
                      });
                      const url = URL.createObjectURL(response.data);
                      const link = document.createElement('a');
                      link.href = url;
                      link.download = `recording-${selectedLog.phoneNumber}-${selectedLog._id}.mp3`;
                      document.body.appendChild(link);
                      link.click();
                      link.remove();
                      URL.revokeObjectURL(url);
                      toast.success('Download started');
                    } catch (error) {
                      toast.error('Failed to download recording');
                    }
                  }}
                >
                  Download
                </Button>
              </div>
            </div>
          )}
        </Modal>
      </div>
    </UserLayout>
  );
};

export default UserCallLogs;